import { env } from '@/shared/lib/env';
import { JSON_API_CONTENT_TYPE, STORAGE_KEYS } from '@/shared/lib/constants';
import { parseErrorsFromResponse } from './jsonapi/errors';

type AuthHeadersProvider = () => {
  token: string | null;
  fingerprint: string | null;
  /** tenantId del JWT (informativo, NON inviato come header). */
  tenantId: string | null;
  lang: string;
};

let authProvider: AuthHeadersProvider = () => ({
  token: null,
  fingerprint: localStorage.getItem(STORAGE_KEYS.fingerprint),
  tenantId: localStorage.getItem(STORAGE_KEYS.tenantId),
  lang: localStorage.getItem(STORAGE_KEYS.lang) ?? env.VITE_DEFAULT_LANG,
});

/** Allows the AuthProvider to inject live token/tenant context into the HTTP client. */
export function setAuthHeadersProvider(provider: AuthHeadersProvider): void {
  authProvider = provider;
}

/**
 * Handler iniettato dall'AuthProvider per il refresh-on-401 (BE v1.8.2: diversi
 * endpoint che prima erano 500 ora rispondono 401). Ritorna il nuovo access token,
 * oppure null se il refresh fallisce (in quel caso l'handler ha già fatto logout).
 */
type RefreshHandler = () => Promise<string | null>;
let refreshHandler: RefreshHandler | null = null;
let refreshInFlight: Promise<string | null> | null = null;

export function setAuthRefreshHandler(handler: RefreshHandler | null): void {
  refreshHandler = handler;
}

/** Single-flight: 401 simultanei condividono lo stesso refresh, non N refresh paralleli. */
function refreshOnce(): Promise<string | null> {
  if (!refreshHandler) return Promise.resolve(null);
  if (!refreshInFlight) {
    refreshInFlight = refreshHandler().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

function getPlatform(): 'web' | 'ios' | 'android' {
  if (typeof window !== 'undefined' && (window as { Capacitor?: { getPlatform: () => string } }).Capacitor) {
    const p = (window as unknown as { Capacitor: { getPlatform: () => string } }).Capacitor.getPlatform();
    if (p === 'ios') return 'ios';
    if (p === 'android') return 'android';
  }
  return 'web';
}

export interface HttpRequestOptions extends Omit<RequestInit, 'body' | 'method'> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Skip JSON serialization (e.g. for FormData uploads). */
  json?: boolean;
  /** Skip Bearer header (e.g. login/refresh). */
  anonymous?: boolean;
  /** Number of retry attempts on 5xx/429. */
  retries?: number;
  /**
   * Endpoint flavor:
   * - 'jsonapi' (default): uses `application/vnd.api+json` for both Accept and Content-Type
   * - 'plain': uses `application/json` (auth endpoints, custom controllers that don't speak JSON:API)
   */
  endpointType?: 'jsonapi' | 'plain';
}

const MAX_RETRIES = 3;
const BACKOFF_BASE_MS = 200;

function isRawBody(b: unknown): b is BodyInit {
  return (
    typeof b === 'string' ||
    (typeof FormData !== 'undefined' && b instanceof FormData) ||
    (typeof Blob !== 'undefined' && b instanceof Blob) ||
    (typeof ArrayBuffer !== 'undefined' && (b instanceof ArrayBuffer || ArrayBuffer.isView(b))) ||
    (typeof URLSearchParams !== 'undefined' && b instanceof URLSearchParams) ||
    (typeof ReadableStream !== 'undefined' && b instanceof ReadableStream)
  );
}

export interface HttpStatusResult<T = unknown> {
  /** Status HTTP grezzo della risposta (204 → body null). */
  status: number;
  /** Body parsato (JSON o testo), oppure null se assente/non parsabile. */
  body: T | null;
}

/**
 * Variante di httpRequest per i workflow custom del BE che comunicano l'esito
 * SOLO via status HTTP, senza documento JSON:API in risposta — es. il workflow
 * POST /api/user-profiles (creazione/associazione utente) che ritorna
 * 200/201/406/409/412/424/428/451 + 400/422 a body vuoto. A differenza di
 * httpRequest NON lancia sugli status >= 400: ritorna { status, body } e lascia
 * la decisione al chiamante. Mantiene Authorization, refresh-on-401
 * (single-flight) e retry su 5xx/429.
 */
export async function httpRequestStatus<T = unknown>(path: string, opts: HttpRequestOptions = {}): Promise<HttpStatusResult<T>> {
  const { method = 'GET', body, anonymous = false, retries = MAX_RETRIES, headers, endpointType = 'jsonapi', ...rest } = opts;
  // Quando il body e' raw (FormData/Blob/ecc), skippiamo automaticamente JSON serialization
  // e lasciamo che il browser/runtime imposti il Content-Type giusto (multipart con boundary).
  const useJson = opts.json !== false && !isRawBody(body);
  const auth = authProvider();

  const contentType = endpointType === 'plain' ? 'application/json' : JSON_API_CONTENT_TYPE;
  const baseHeaders: Record<string, string> = {
    Accept: useJson ? contentType : '*/*',
    'platform': getPlatform(),
    'userLang': auth.lang,
  };
  if (useJson && body !== undefined) {
    baseHeaders['Content-Type'] = contentType;
  }
  if (auth.fingerprint) baseHeaders['fingerPrint'] = auth.fingerprint;
  if (!anonymous && auth.token) baseHeaders['Authorization'] = `Bearer ${auth.token}`;
  // NOTA: il cross-tenant override (BE v1.8.3+) si applica via filter JSON:API
  // su tenant.id (list) o data.relationships.tenant (POST), NON via header HTTP.
  // Eccezione: i workflow custom (es. user-profiles) usano gli header TenantId/
  // TenantDestinationId esplicitamente passati dal chiamante in opts.headers.

  const url = path.startsWith('http') ? path : `${env.VITE_API_BASE_URL}${path}`;

  const serializedBody: BodyInit | undefined =
    body === undefined ? undefined : isRawBody(body) ? body : JSON.stringify(body);

  let attempt = 0;
  let did401Retry = false;
  let lastErr: unknown;
  while (attempt <= retries) {
    try {
      const response = await fetch(url, {
        ...rest,
        method,
        headers: { ...baseHeaders, ...(headers as Record<string, string> | undefined) },
        body: serializedBody,
      });

      if (response.status === 204) return { status: 204, body: null };

      const respContentType = response.headers.get('content-type') ?? '';
      const isJsonResp = respContentType.includes('json');
      const parsed = isJsonResp ? await response.json().catch(() => null) : await response.text().catch(() => null);

      // 401 → un solo tentativo di refresh + retry. Skip per le richieste anonymous
      // (login/refresh/change-tenant), così l'endpoint di refresh non innesca un loop.
      if (response.status === 401 && !anonymous && !did401Retry && refreshHandler) {
        const newToken = await refreshOnce();
        if (newToken) {
          did401Retry = true;
          baseHeaders['Authorization'] = `Bearer ${newToken}`;
          continue; // ritenta con il token fresco, senza consumare i retry 5xx
        }
        // refresh fallito: l'handler ha già fatto logout/redirect → propaga lo status 401
      }
      if ((response.status >= 500 || response.status === 429) && attempt < retries) {
        const retryAfter = Number(response.headers.get('Retry-After')) || BACKOFF_BASE_MS * 2 ** attempt;
        await sleep(retryAfter);
        attempt++;
        continue;
      }
      return { status: response.status, body: parsed as T | null };
    } catch (err) {
      // Network-level failure: retry with backoff
      lastErr = err;
      if (attempt >= retries) break;
      await sleep(BACKOFF_BASE_MS * 2 ** attempt);
      attempt++;
    }
  }
  throw lastErr ?? new Error('Network error');
}

export async function httpRequest<T = unknown>(path: string, opts: HttpRequestOptions = {}): Promise<T> {
  const { status, body } = await httpRequestStatus<T>(path, opts);
  if (status === 204) return undefined as T;
  if (status >= 400) throw parseErrorsFromResponse(status, body);
  return body as T;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Helper tipato per multipart upload. Sostituisce il pattern
 *   httpRequest(url, { method: 'POST', body: form as unknown as BodyInit, json: false, headers: {} })
 * con
 *   httpFormRequest<Resp>(url, form)
 * Lascia che il runtime decida boundary/Content-Type del multipart.
 */
export function httpFormRequest<T = unknown>(
  path: string,
  formData: FormData,
  opts: Omit<HttpRequestOptions, 'body' | 'json' | 'method'> & { method?: 'POST' | 'PUT' | 'PATCH' } = {},
): Promise<T> {
  return httpRequest<T>(path, { ...opts, method: opts.method ?? 'POST', body: formData });
}
